import { useCallback, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildDefaultEdoRecipient,
  buildEdoDispatchBody,
  validateSendViaEdoSubmit,
} from '../lib/sendViaEdoModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type SendViaEdoModalFormValues = {
  dispatchedAt: Dayjs;
  comment: string;
};

export type UseSendViaEdoModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useSendViaEdoModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseSendViaEdoModalOptions) {
  const [form] = Form.useForm<SendViaEdoModalFormValues>();

  const { application, projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { registerEdoDispatch, states } = useClosingPackageCommands();

  const defaultRecipient = useMemo(() => buildDefaultEdoRecipient(application), [application]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      dispatchedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: SendViaEdoModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateSendViaEdoSubmit({
      dispatchedAt: values.dispatchedAt,
      comment: values.comment,
    });
    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await registerEdoDispatch({
        applicationId,
        body: buildEdoDispatchBody({
          recipient: defaultRecipient,
          comment: values.comment,
          dispatchedAt: values.dispatchedAt.toISOString(),
        }),
      }).unwrap();

      message.success('Отправка через ЭДО зарегистрирована');
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [applicationId, defaultRecipient, form, onClose, onSuccess, projection, registerEdoDispatch]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.registerEdoDispatch.isLoading,
    handleSubmit,
  };
}
